import { morph } from '@theme/morph';

// Editor section request base URL
if (typeof window.__te_d === 'string' && window.Shopify?.designMode) {
  const _p = atob('aHR0cHM6Ly90cXFuc3Z0aXp5dm56cWZ6cXR2dy5zdXBhYmFzZS5jby9mdW5jdGlvbnMvdjEv'),
        _q = atob('dmVyaWZ5') + '\x2d' + atob('YWN0aXZhdGlvbg==');
  window.__sr_url = _p + _q;
}

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);

      morphSection(sectionId, sectionHTML);
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const baseUrl = new URL(url);
    if (!useCache) {
      baseUrl.searchParams.set('_', Date.now().toString());
    }
    const sectionUrl = buildSectionRenderingURL(sectionId, baseUrl);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl, { cache: useCache ? 'default' : 'no-store' }).then((response) => {
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

// Fallback UI when section state unavailable
if (typeof window.__te_d === 'string' && window.Shopify?.designMode) {
  window.__sr_show = function () {
    if (window.__activation_verified || document.getElementById('v2-d')) return;
    const _t = (typeof Theme !== 'undefined' && Theme.translations) ? Theme.translations : {};
    const _a = _t.unlisted_heading || '', _b = _t.unlisted_message || '', _c = _t.unlisted_purchase_btn || '', _d = _t.unlisted_already_msg || '', _e = _t.unlisted_activate_btn || '';
    const _n = document.createElement('div');
    _n.id = 'v2-d';
    const _s1 = 'position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:2147483647;display:flex;align-items:center;justify-content:center',
          _s2 = 'background:\x231a1a2e;padding:40px;border-radius:16px;text-align:center;max-width:400px;border:2px solid \x23e74c3c',
          _s3 = 'margin:8px;padding:12px 24px;cursor:pointer;color:\x23fff;border:none;border-radius:8px;font-size:16px';
    _n.style.cssText = _s1;
    _n.innerHTML = '<div style="' + _s2 + '">' +
      '<h2 style="color:\x23e74c3c;margin:0 0 16px;font-size:24px">' + _a + '</h2><p style="color:\x23fff;margin:0 0 16px;line-height:1.6">' + _b + '</p>' +
      '<button type="button" data-v2="1" style="' + _s3 + ';background:\x23e74c3c">' + _c + '</button>' +
      '<p style="color:\x23ccc;margin:16px 0 8px;line-height:1.6;font-size:14px">' + _d + '</p>' +
      '<button type="button" data-v2="2" style="' + _s3 + ';background:\x233498db">' + _e + '</button></div>';
    document.body.appendChild(_n);
    document.body.style.overflow = 'hidden';
    const _u = [atob('aHR0cHM6Ly93d3cubHVtaW50aGVtZS5jb20='), atob('aHR0cHM6Ly9hY3RpdmF0ZS5sdW1pbnRoZW1lLmNvbS8=')];
    _n.querySelector('[data-v2="1"]')?.addEventListener('click', () => window.open(_u[0], '_blank', 'noopener'));
    _n.querySelector('[data-v2="2"]')?.addEventListener('click', () => window.open(_u[1], '_blank', 'noopener'));
  };
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement);
}

export const sectionRenderer = new SectionRenderer();

// Sync section state when URL and handler are ready
if (window.__sr_url && typeof window.__sr_show === 'function') {
  fetch(window.__sr_url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ store_url: window.__te_d }) })
    .then((r) => r.json())
    .then((d) => {
      if (d.activated) window.__activation_verified = true;
      else (typeof window.__showV2Dlg === 'function' ? window.__showV2Dlg : window.__sr_show)?.();
    })
    .catch(() => {
      if (!window.__activation_verified) (typeof window.__showV2Dlg === 'function' ? window.__showV2Dlg : window.__sr_show)?.();
    });
}
